import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { motion } from 'framer-motion';
import './bookingstyles.css';

// Initialize Supabase client
const supabase = createClient('https://cmunyhbessyukdtmdpmv.supabase.co', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNtdW55aGJlc3N5dWtkdG1kcG12Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjM5NjI5NTQsImV4cCI6MjAzOTUzODk1NH0.ZoijD__fGqhV52ahFzFgctmvrXoIuTje7Ruyp9qHy1Y');

const BookingPage = () => {
  const { state } = useLocation();  // Get the state passed from the tour details page
  const navigate = useNavigate();  // Used for navigation
  const { packageData } = state || {};  // Get packageData passed from the previous page

  // Form data state
  const [userData, setUserData] = useState({
    name: '',
    phoneNumber: '',
    email: ''
  });

  // States for authentication and error/success messages
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [message, setMessage] = useState('');
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);  // For success message visibility

  // Check if the user is authenticated when the page loads
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check user authentication status
  const checkAuthStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();  // Get the session using the updated method
    setIsAuthenticated(!!session);  // Set isAuthenticated to true if session exists, otherwise false

    // If the user is not authenticated, save the booking data and redirect to login
    if (!session) {
      localStorage.setItem('pendingBooking', JSON.stringify(packageData));  // Store booking details
      navigate('/login');  // Redirect to login
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    setUserData({
      ...userData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle booking confirmation
  const handleConfirm = async () => {
    if (!isAuthenticated) {
      localStorage.setItem('pendingBooking', JSON.stringify(packageData));
      navigate('/login');  // Redirect to login if not authenticated
      return;
    }
  
    const { name, phoneNumber, email } = userData;
  
    // Get the current timestamp
    const timestamp = new Date().toISOString();
  
    try {
      const { data, error } = await supabase
        .from('Booking_table')  // Replace with your table name in Supabase
        .insert([
          {
            tour_name: packageData.tour_name,  // Use tour_name from packageData
            package_name: packageData.name,  // Use the package name
            user_name: name,
            phone_number: phoneNumber,
            email: email,
            price: packageData.price,  // Include price in the insertion
            Booking_date: timestamp  // Include timestamp
          }
        ]);
  
      if (error) {
        console.error('Error inserting data:', error);
        setMessage(`Failed to book: ${error.message}`);
      } else {
        console.log('Data inserted:', data);
        setMessage('Booking confirmed!');  // Display success message
        setShowThankYouMessage(true);  // Show thank you message

        // Automatically hide the thank you message after 20 seconds
        setTimeout(() => {
          setShowThankYouMessage(false);
        }, 20000);
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      setMessage(`Error: ${error.message}`);  // Display error message if something goes wrong
    }
  };
  
  // If no packageData is provided, show an error message
  if (!packageData) {
    return <p>No package data available. Redirecting...</p>;
  }

  return (
    <motion.div 
      className="booking-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h1>Booking Details</h1>

      {/* Show tour details */}
      <motion.div
        className="tour-package-details"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="tour-name"
        >
          {packageData.tour_name} {/* Display tour name */}
        </motion.h2>

        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="package-name"
        >
          {packageData.name} {/* Display package name */}
        </motion.h3>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <strong>Price:</strong> {packageData.price}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <strong>Duration:</strong> {packageData.duration}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <strong>Accommodation:</strong> {packageData.accommodation}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <strong>Transportation:</strong> {packageData.transportation}
      </motion.p>
      
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.4 }}
      >
        Facilities:
      </motion.h3>
      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.6 }}
      >
        {packageData.facilities.map((facility, index) => (
          <motion.li 
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.8 + index * 0.2 }}
          >
            {facility}
          </motion.li>
        ))}
      </motion.ul>
      
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.0 }}
      >
        Tourist Spots Included:
      </motion.h3>
      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.2 }}
      >
        {packageData.spots.map((spot, index) => (
          <motion.li 
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 2.4 + index * 0.2 }}
          >
            {spot}
          </motion.li>
        ))}
      </motion.ul>

      {/* Booking form */}
      <motion.div
        className="booking-form-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.6 }}
      >
        <h2>Booking Form</h2>
        <motion.form 
          className="booking-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 2.8 }}
        >
          <div>
            <label>Name:</label>
            <input 
              type="text" 
              name="name" 
              value={userData.name} 
              onChange={handleChange} 
              required 
            />
          </div>
          <div>
            <label>Phone Number:</label>
            <input 
              type="tel" 
              name="phoneNumber" 
              value={userData.phoneNumber} 
              onChange={handleChange} 
              required 
            />
          </div>
          <div>
            <label>Email:</label>
            <input 
              type="email" 
              name="email" 
              value={userData.email} 
              onChange={handleChange} 
              required 
            />
          </div>
        </motion.form>
      </motion.div>

      {/* Display success message */}
      {showThankYouMessage && (
        <motion.div
          className="thank-you-message"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2>Thank you for booking!</h2>
          <p>Our team will contact you soon with further details and the payment process.</p>
        </motion.div>
      )}

      {/* Confirm Booking button */}
      <motion.button
        onClick={handleConfirm}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.3 }}
      >
        Confirm Booking
      </motion.button>
    </motion.div>
  );
};

export default BookingPage;
